// The Geofences card over the live map (user request, instead of a menu item and a separate
// page): small when closed; open, it switches geofences on or off on the maps, lists them and
// creates or changes one right there – the card becomes the form and the shape is drawn on the
// live map itself. Copied in web/ (without the owner choice) and crm/.
import { CSSProperties, FormEvent, useEffect, useMemo, useState } from 'react';
import { Customer, customers as customersApi, Geofence, GEOFENCE_COLOURS, GeofenceColour, geofences as api, GeofenceInput } from '../api';
import { Circle, Drawing, Points } from './GeofenceEditor';
import { reloadGeofences, useGeofences, useGeofencesShown } from './geofenceStore';
import { Link } from './Links';
import { SearchSelect } from './SearchSelect';

const LISTED = 6;
const COLOUR_NAME: Record<GeofenceColour, string> = { blue: 'Blue', green: 'Green', orange: 'Orange', purple: 'Purple', red: 'Red', teal: 'Teal' };

export interface Draft {
  id: number | null;            // null: a new geofence
  name: string;
  colour: GeofenceColour;
  customerId: number | null;    // null: company-wide (staff only)
  kind: 'circle' | 'polygon';
  circle: Circle | null;
  points: Points;
  shapeTouched: boolean;
}
type GoTo = { key: number; lat: number; lon: number } | null;

const fromGeofence = (g: Geofence): Draft => ({
  id: g.id, name: g.name, colour: g.colour, customerId: g.customer?.id ?? null, kind: g.kind,
  circle: g.circle, points: g.kind === 'polygon' ? g.points : [], shapeTouched: false,
});
const blank = (): Draft => ({ id: null, name: '', colour: 'blue', customerId: null, kind: 'circle', circle: null, points: [], shapeTouched: false });

// The page holds the draft, so the map can draw it and the card can edit it.
export function useGeofenceDraft() {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [goTo, setGoTo] = useState<GoTo>(null);
  const drawing: Drawing | null = draft && {
    kind: draft.kind, circle: draft.circle, points: draft.points, colour: draft.colour, fitOnStart: draft.id !== null,
    onCircle: (c) => setDraft((d) => d && { ...d, circle: c, shapeTouched: true }),
    onPoints: (p) => setDraft((d) => d && { ...d, points: p, shapeTouched: true }),
  };
  return { draft, setDraft, goTo, setGoTo, drawing };
}

export function GeofenceCard({ draft, setDraft, setGoTo, canDraw }: {
  draft: Draft | null;
  setDraft: (d: Draft | null | ((d: Draft | null) => Draft | null)) => void;
  setGoTo: (g: GoTo) => void;
  canDraw: boolean;   // false on the OpenStreetMap map: drawing needs Google
}) {
  const all = useGeofences();
  const [shown, setShown] = useGeofencesShown();
  const [open, setOpen] = useState(false);
  const sorted = useMemo(() => [...all].sort((a, b) => a.name.localeCompare(b.name, 'en-IE', { numeric: true })), [all]);
  // Opening a draft opens the card; it can then be folded and opened again without losing it.
  useEffect(() => { if (draft) setOpen(true); }, [draft !== null]);

  return (
    <details className={`map-key geo-card${draft ? ' drawing' : ''}`} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      {/* While drawing, the card can be folded away to see more map; its title says why. */}
      <summary>{draft && !open ? 'Drawing a geofence…' : <>Geofences{all.length > 0 && <span className="muted"> · {all.length}</span>}</>}</summary>
      {draft ? <DraftForm draft={draft} setDraft={setDraft} setGoTo={setGoTo} /> : (
        <>
          <label className="check geo-card-switch">
            <input type="checkbox" checked={shown} onChange={(e) => setShown(e.target.checked)} />Show on map
          </label>
          {sorted.length === 0
            ? <p className="muted">None yet. Draw your yard or a customer's site to see when vehicles arrive and leave.</p>
            : (
              <ul>{sorted.slice(0, LISTED).map((g) => (
                <li key={g.id}>
                  <button type="button" className="link geo-card-item" disabled={!canDraw} onClick={() => setDraft(fromGeofence(g))} title={`Change ${g.name}`}>
                    <span className="geo-dot" aria-hidden="true" style={{ '--geo': `var(--geofence-${g.colour})` } as CSSProperties} />{g.name}
                  </button>
                </li>
              ))}</ul>
            )}
          {canDraw ? (
            <div className="geo-card-actions">
              <button type="button" className="primary small" onClick={() => setDraft(blank())}>Create geofence</button>
              {sorted.length > 0 && <Link to="/geofences">{sorted.length > LISTED ? `All ${sorted.length}` : 'Manage'}</Link>}
            </div>
          ) : <p className="muted">Drawing geofences needs the Google map, which isn't available here.</p>}
        </>
      )}
    </details>
  );
}

function DraftForm({ draft: d, setDraft, setGoTo }: {
  draft: Draft;
  setDraft: (d: Draft | null | ((d: Draft | null) => Draft | null)) => void;
  setGoTo: (g: GoTo) => void;
}) {
  const [custs, setCusts] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<Array<{ label: string; lat: number; lon: number }> | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => { customersApi.list().then((r) => setCusts(r.customers)).catch(() => {}); }, []);
  const ownerOptions = useMemo(() => custs.map((c) => ({ value: String(c.id), label: c.name })), [custs]);
  const set = (patch: Partial<Draft>) => setDraft((cur) => cur && { ...cur, ...patch });

  const find = async () => {
    setFound(null);
    try {
      const r = await api.searchPlaces(search);
      if (r.results.length === 1) setGoTo({ key: Date.now(), ...r.results[0] });
      else setFound(r.results);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const ready = d.kind === 'circle' ? d.circle !== null : d.points.length >= 3;
    if (!ready) { setError(d.kind === 'circle' ? 'Click on the map to place the circle.' : 'Click on the map for at least 3 corners.'); return; }
    setBusy(true);
    setError('');
    const body: GeofenceInput = { name: d.name.trim(), colour: d.colour, customerId: d.customerId };
    if (d.id === null || d.shapeTouched) Object.assign(body, d.kind === 'circle' ? { circle: d.circle! } : { points: d.points });
    try {
      if (d.id === null) await api.add(body);
      else await api.update(d.id, body);
      await reloadGeofences();
      setDraft(null);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.remove(d.id!);
      await reloadGeofences();
      setDraft(null);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <form className="geo-card-form" onSubmit={save}>
      <p className="geo-card-title">{d.id === null ? 'New geofence' : 'Change geofence'}</p>
      <label>Name<input id="gc-name" type="text" required maxLength={80} value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Letterkenny yard" /></label>
      <label htmlFor="gc-owner">Owner</label>
      <SearchSelect id="gc-owner" value={d.customerId === null ? '' : String(d.customerId)} options={ownerOptions}
        emptyLabel="Company-wide (staff only)" onChange={(v) => set({ customerId: v ? Number(v) : null })} />
      <fieldset className="geo-card-colours">
        <legend>Colour</legend>
        {GEOFENCE_COLOURS.map((c) => (
          <label key={c} className="geo-swatch" title={COLOUR_NAME[c]}>
            <input type="radio" name="gc-colour" value={c} checked={d.colour === c} onChange={() => set({ colour: c })} aria-label={COLOUR_NAME[c]} />
            <span className="geo-dot" aria-hidden="true" style={{ '--geo': `var(--geofence-${c})` } as CSSProperties} />
          </label>
        ))}
      </fieldset>
      <div className="seg" role="group" aria-label="Shape">
        <button type="button" aria-pressed={d.kind === 'circle'} onClick={() => set({ kind: 'circle', shapeTouched: true })}>Circle</button>
        <button type="button" aria-pressed={d.kind === 'polygon'} onClick={() => set({ kind: 'polygon', shapeTouched: true })}>Polygon</button>
      </div>
      {d.kind === 'circle' ? (
        <>
          <p className="hint">Click the map for the centre, then drag the centre or the edge.</p>
          <label>Radius (m)<input id="gc-radius" type="number" min={50} max={50000} step={10} disabled={!d.circle} value={d.circle?.radiusM ?? ''}
            onChange={(e) => d.circle && set({ circle: { ...d.circle, radiusM: Number(e.target.value) }, shapeTouched: true })} /></label>
        </>
      ) : (
        <>
          <p className="hint">Click the map for each corner. Drag a corner to move it; double-click one to take it out.</p>
          <p className="small">{d.points.length} corner{d.points.length === 1 ? '' : 's'}
            {d.points.length > 0 && <> · <button type="button" className="link" onClick={() => set({ points: d.points.slice(0, -1), shapeTouched: true })}>Undo</button>
              {' '}· <button type="button" className="link" onClick={() => set({ points: [], shapeTouched: true })}>Start again</button></>}</p>
        </>
      )}
      <div className="geo-card-search">
        <label className="sr-only" htmlFor="gc-search">Find a place</label>
        <input id="gc-search" type="search" placeholder="Find a place…" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void find(); } }} />
        <button type="button" onClick={() => void find()} disabled={search.trim().length < 3}>Find</button>
      </div>
      {found && (
        <ul className="geo-found">
          {found.length === 0 && <li className="muted">Nothing found.</li>}
          {found.map((r) => <li key={r.label}><button type="button" className="link" onClick={() => { setGoTo({ key: Date.now(), ...r }); setFound(null); }}>{r.label}</button></li>)}
        </ul>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="geo-card-actions">
        <button className="primary small" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="link" onClick={() => setDraft(null)}>Cancel</button>
        {d.id !== null && !confirmRemove && <button type="button" className="link danger" onClick={() => setConfirmRemove(true)}>Remove…</button>}
      </div>
      {confirmRemove && (
        <div className="confirm" role="alert">
          <p>Remove it? It stops applying now; past entries and exits keep its name.</p>
          <button type="button" className="danger small" disabled={busy} onClick={() => void remove()}>Remove</button>
          <button type="button" className="link" onClick={() => setConfirmRemove(false)}>Keep it</button>
        </div>
      )}
    </form>
  );
}
